import { Date as DateType } from './common-types';

export const formatName = ({
	firstName,
	middleName,
	lastName,
}: {
	firstName: string | null;
	middleName: string | null;
	lastName: string | null;
}) => {
	return `${firstName}${
		middleName ? ' ' + middleName : ''
	} ${lastName}`.trim();
};

/**
 * Format a date string for display.
 * @param dateString - ISO date string, YYYY-MM-DD, or object with $date
 * @param withTime - Include time (hour/minute, 12-hour)
 * @param timeZone - IANA timezone (e.g. 'America/Los_Angeles'). If omitted, uses the user's local timezone.
 */
export const formatDate = (
	dateString: string | DateType | undefined | null,
	withTime = false,
	timeZone?: string
) => {
	if (dateString === undefined || dateString === null) return null;

	let parsedDateString: string | DateType = dateString;

	if (typeof parsedDateString === 'object' && '$date' in parsedDateString)
		parsedDateString = parsedDateString.$date;

	const htmlDateInputRegex = /^\d{4}-\d{2}-\d{2}$/;
	const dbUTCDateRegex1 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
	const dbUTCDateRegex2 = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

	if (htmlDateInputRegex.test(parsedDateString)) {
		const [year, month, day] = parsedDateString.split('-');

		return `${month}/${day}/${year}`;
	} else if (
		dbUTCDateRegex1.test(parsedDateString) ||
		dbUTCDateRegex2.test(parsedDateString)
	) {
		const resolvedTimeZone =
			timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
		return new Intl.DateTimeFormat([], {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: withTime ? 'numeric' : undefined,
			minute: withTime ? 'numeric' : undefined,
			hour12: withTime ? true : undefined,
			timeZone: resolvedTimeZone,
		}).format(new Date(parsedDateString));
	} else return parsedDateString;
};

export const capitalize = (string: string | undefined | null) => {
	if (string) return string.charAt(0).toUpperCase() + string.slice(1);

	return 'N/A';
};

export const convertDateTimeToYYYYMMDD = (datetimeString: string) => {
	const date = new Date(datetimeString);
	const isoDateString = date.toISOString().split('T')[0];

	return isoDateString;
};

/**
 * Calculates the end date by adding a specified number of business days to a start date, excluding weekends.
 * @param startDate - The start date in YYYY-MM-DD format
 * @param businessDays - The number of business days to add
 * @returns The end date in YYYY-MM-DD format
 */
export const addBusinessDays = (
	startDate: string,
	businessDays: number
): string => {
	const start = new Date(startDate + 'T00:00:00');
	const current = new Date(start);
	let daysAdded = 0;

	while (daysAdded < businessDays) {
		current.setDate(current.getDate() + 1);
		const dayOfWeek = current.getDay();
		// 0 = Sunday, 6 = Saturday
		if (dayOfWeek !== 0 && dayOfWeek !== 6) {
			daysAdded++;
		}
	}

	// Format as YYYY-MM-DD
	const year = current.getFullYear();
	const month = String(current.getMonth() + 1).padStart(2, '0');
	const day = String(current.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

/**
 * Calculates the number of business days between two dates, excluding weekends.
 * @param startDate - The start date in YYYY-MM-DD format (inclusive)
 * @param endDate - The end date in YYYY-MM-DD format (inclusive)
 * @returns The number of business days between the two dates
 */
export const calculateBusinessDaysBetween = (
	startDate: string,
	endDate: string
): number => {
	let start = new Date(startDate + 'T00:00:00');
	let end = new Date(endDate + 'T00:00:00');

	// Ensure start is before end
	if (start > end) {
		const temp = start;
		start = end;
		end = temp;
	}

	let businessDays = 0;
	const current = new Date(start);

	while (current <= end) {
		const dayOfWeek = current.getDay();
		// 0 = Sunday, 6 = Saturday
		if (dayOfWeek !== 0 && dayOfWeek !== 6) {
			businessDays++;
		}
		current.setDate(current.getDate() + 1);
	}

	return businessDays;
};

/**
 * Today's calendar date in America/Los_Angeles as YYYY-MM-DD - the lab's day,
 * regardless of the browser's timezone (and unlike toISOString(), which rolls
 * over to tomorrow in the evening Pacific time).
 */
export const getTodayInLosAngeles = (): string =>
	new Date().toLocaleDateString('en-CA', { timeZone: 'America/Los_Angeles' });
